




import React, { useMemo } from "react";
import { Box, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { ResponsiveContainer, ComposedChart, XAxis, YAxis, Tooltip, Line, CartesianGrid } from "recharts";
import { format } from "date-fns";

interface DataCardProps {
    name?: string;
    reversed?: boolean;
    chartData: {
        date: string;
        value: number | null;

    }[];
}


const DataCard: React.FC<DataCardProps> = ({ chartData, name, reversed = false }) => {

    const theme = useTheme();

    if (!chartData || chartData.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography color="text.secondary">No data available</Typography>
            </Box>
        );
    }

    const averageValue = useMemo(() => {
        return chartData.reduce((acc, curr) => acc + (curr.value ?? 0), 0) / chartData.length;
    }, [chartData]);

    const latestValue = useMemo(() => {
        return chartData[chartData.length - 1].value ?? 0;
    }, [chartData]);

    const latestDate = useMemo(() => {
        return chartData[chartData.length - 1].date;
    }, [chartData]);

    return (
        <Stack sx={{
            position: 'relative',
            flex: 1,
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            padding: 3,
            gap: 2,
        }}>
            <Stack sx={{ mb: 1 }}>
                <Typography variant="h6" color="text.secondary">
                    {name ?? 'Untitled'}
                </Typography>


                <Stack direction="row" spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        {latestValue.toFixed(0)} on {format(new Date(latestDate), 'dd/MM/yyyy')}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        {averageValue.toFixed(1)} average
                    </Typography>
                </Stack>
            </Stack>
            <Box sx={{ width: '100%', height: '150px' }}>

                <ResponsiveContainer>
                    <ComposedChart data={chartData}>

                        <CartesianGrid strokeDasharray="0 0" stroke={theme.palette.divider} vertical={false} />

                        <YAxis
                            yAxisId="right"
                            reversed={reversed}
                            orientation="right"
                            width={20}
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                            fontFamily={theme.typography.fontFamily}
                            allowDecimals={false}
                            interval={0}
                        />
                        <Tooltip />


                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="value"
                            name={name}
                            stroke={theme.palette.secondary.main}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                        />

                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
        </Stack>
    );
};

export default DataCard;

